import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Warning, AccountBalance, AccessTime } from '@mui/icons-material';
import apiService from '../services/apiService';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend);

const ArrearsAging = () => {
  useAuth(); // ensure authenticated
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [sortBy, setSortBy] = useState('lateDays');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/arrears-aging-report`
      );
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Error fetching arrears aging report:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const sortDelinquent = (data) => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const { summary, agingBuckets, balanceComposition, topDelinquent } = reportData;

  // Doughnut chart for balance composition
  const balanceTotal = balanceComposition.principal + balanceComposition.interest + balanceComposition.fee + balanceComposition.penalty;
  const doughnutData = {
    labels: ['Principal', 'Interest', 'Fees', 'Penalties'],
    datasets: [{
      data: [balanceComposition.principal, balanceComposition.interest, balanceComposition.fee, balanceComposition.penalty],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const pct = balanceTotal > 0 ? ((value / balanceTotal) * 100).toFixed(1) : 0;
            return `${label}: ${formatCurrency(value)} (${pct}%)`;
          }
        }
      }
    }
  };

  // Bar chart data for aging buckets
  const barData = agingBuckets.map(b => ({
    bucket: b.bucket + ' days',
    'Loan Count': b.loanCount,
    'Arrears Amount': b.totalArrears
  }));

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Arrears Aging & Portfolio Health</h1>
          <p className="text-gray-600 mt-1">Current snapshot of overdue loan portfolio by aging buckets</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Loans at Risk</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.totalLoansAtRisk}</p>
                <p className="text-sm text-red-600 mt-1">Active late loans</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <Warning className="text-red-600 w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Arrears</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(summary.totalArrears)}</p>
                <p className="text-sm text-orange-600 mt-1">Outstanding amount</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <AccountBalance className="text-orange-600 w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Average Late Days</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary.avgLateDays}</p>
                <p className="text-sm text-blue-600 mt-1">Days overdue</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <AccessTime className="text-blue-600 w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Aging Buckets Bar Chart */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Arrears by Aging Bucket</h3>
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" />
                  <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#EF4444" />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      if (name === 'Arrears Amount') return formatCurrency(value);
                      return value;
                    }}
                  />
                  <RechartsLegend />
                  <Bar yAxisId="left" dataKey="Loan Count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="Arrears Amount" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Balance Composition Doughnut */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Balance Composition</h3>
            <div style={{ height: 300 }}>
              {balanceTotal > 0 ? (
                <Doughnut data={doughnutData} options={doughnutOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">No balance data</div>
              )}
            </div>
          </div>
        </div>

        {/* Aging Buckets Summary Table */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Aging Bucket Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Bucket</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Loan Count</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Arrears Amount</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {agingBuckets.map((bucket, index) => (
                  <tr key={bucket.bucket} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${COLORS[index]}20`, color: COLORS[index] }}>
                        {bucket.bucket} days
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700 font-medium">{bucket.loanCount}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700 font-medium">{formatCurrency(bucket.totalArrears)}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">
                      {summary.totalArrears > 0 ? ((bucket.totalArrears / summary.totalArrears) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold">
                  <td className="py-3 px-4 text-sm text-gray-900">Total</td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900">{summary.totalLoansAtRisk}</td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(summary.totalArrears)}</td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Top 20 Delinquent Accounts */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 20 Delinquent Accounts</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {[
                    { key: 'loanNumber', label: 'Loan #' },
                    { key: 'clientName', label: 'Client' },
                    { key: 'outstandingAmount', label: 'Outstanding' },
                    { key: 'lateDays', label: 'Late Days' },
                    { key: 'assignedAgent', label: 'Assigned Agent' }
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={`${col.key === 'outstandingAmount' || col.key === 'lateDays' ? 'text-right' : 'text-left'} py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:text-cyan-600`}
                    >
                      {col.label} {sortBy === col.key && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortDelinquent(topDelinquent).map((loan, index) => (
                  <tr key={loan.loanId || index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => navigate(`/loan/${loan.loanId}`)}
                        className="text-cyan-600 hover:text-cyan-800 font-medium text-sm"
                      >
                        {loan.loanNumber}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">{loan.clientName}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700 font-medium">{formatCurrency(loan.outstandingAmount)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        loan.lateDays > 90 ? 'bg-red-100 text-red-800' :
                        loan.lateDays > 30 ? 'bg-orange-100 text-orange-800' :
                        loan.lateDays > 14 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {loan.lateDays} days
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">{loan.assignedAgent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topDelinquent.length === 0 && (
              <div className="text-center py-8 text-gray-500">No delinquent accounts found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArrearsAging;
